import Map from '@/infra/map/Map';
import UserCurrentLocationPin from '@/infra/map/pins/UserCurrentLocationPin';
import bezier from '@turf/bezier-spline';
import midpoint from '@turf/midpoint';
import distance from '@turf/distance';
import destination from '@turf/destination';
import { point, lineString } from '@turf/helpers';

/**
 * 地図の mutations
 */
const mutations = {

  /**
   * 地図を初期化します
   * @param state
   * @param payload
   */
  init: (state, payload) => {
    state.map = new Map(payload.id, payload.language, payload.serviceId, payload.center, payload.zoom, payload.menu,
      payload.onAnnotationClick);
  },

  /**
   * 地図を削除します
   * @param {Object} state
   */
  removeMap: (state) => {
    state.map.destroy();
    state.map = null;
  },

  /**
   * ズームアップします
   * @param {Object} state
   */
  zoomUp: (state) => {
    state.map.zoomUp();
  },

  /**
   * ズームダウンします
   * @param {Object} state
   */
  zoomDown: (state) => {
    state.map.zoomDown();
  },

  /**
   * 中心を移動します
   * @param {Object} state
   * @param {Object} payload
   */
  panTo: (state, payload) => {
    if (!payload || !payload.coord || !state.map) return;
    state.map._map.setCenter(payload.coord, true);
  },

  /**
   * 緯度経度の配列を含むように縮尺と中心を調整します
   * @param {Object} state
   * @param {Object} payload
   */
  setFocus: (state, payload) => {
    if (!state.map || !state.map._map) {
      return;
    }
    state.map.setFocus(payload.path);
  },

  /**
   * 地図の表示領域サイズ変更を行います。
   */
  resize: (state, payload = {}) => {
    if (!state.map || !state.map._map) {
      return;
    }
    state.map.resize(payload.width, payload.height);
  },

  /**
   * ピンを追加します
   * <pre>
   * payload に応じて適切なピンオブジェクトを生成します
   * </pre>
   * @param {Object} state
   * @param {Object} payload
   */
  addPin: (state, payload = {}) => {
    if (!state.map || !state.map._map) {
      return;
    }
    state.map._map.addGLMarker(payload);
  },

  /**
   * ピンを複数件追加します
   * <pre>
   * payload に応じて適切なピンオブジェクトを生成します
   * </pre>
   * @param {Object} state
   * @param {Object} payload
   */
  addPins: (state, payload) => {
    if (!state.map || !state.map._map) {
      return;
    }
    state.map._map.addGLMarkers(payload);
  },

  /**
   * (addPinsは順次こちらに変更していく)
   * model/spot 形式のスポット情報を受け取り、
   * 現在地を表すスポット用ピンを地図に追加します
   * @param {Object} state
   * @param {Object} payload
   */
  addUserCurrentLocationPinBySpot: (state, payload) => {
    if (!state.map || !state.map._map || !payload) {
      return;
    }
    state.pins.push(
      new UserCurrentLocationPin(state.map._map, payload)
    );
  },

  /**
   * 指定したピンを削除します
   * @param {Object} state
   * @param {GLMarker} payload
   */
  removePinFromAction: (state, payload) => {
    if (!payload) return;
    state.map._map.removeGLMarker(payload);
  },

  /**
   * 指定した種別のピンを削除します
   * @param {Object} state
   * @param {Object} payload
   */
  removePins: (state, payload) => {
    if (!payload) return;
    state.map._map.removeGLMarkers(payload);
  },

  /**
   * ポリラインを追加します
   * <pre>
   * payload に応じて適切なポリラインオブジェクトを生成します
   * </pre>
   * @param {Object} state
   * @param {Object} payload
   */
  addPolyline: (state, payload = {}) => {
    if (payload.geoJson) {
      // ルート形状が道を追従しない移動手段はベジエ曲線に書き換え
      payload.geoJson.features.some((feature, index) => {
        const paintsBezierFeature = feature?.properties?.transport_type === 'airplane' &&
          feature.geometry.coordinates.length === 2;
        if (paintsBezierFeature) {
          // /comparison からルート形状を取得した場合、nt-mocha-beans で緯度経度の値を文字列で返却しているため、Float に変換
          const start = feature.geometry.coordinates[0].map(num => parseFloat(num));
          const goal = feature.geometry.coordinates[1].map(num => parseFloat(num));

          const s = point(start);
          const g = point(goal);
          const mid = midpoint(s, g);
          const d = distance(s, g);
          const t = destination(mid, d / 4, -d / 6);
          const line = lineString([s.geometry.coordinates, t.geometry.coordinates, g.geometry.coordinates]);
          const newGeo = bezier(line);
          newGeo.properties = feature.properties;
          newGeo.bbox = feature.bbox;
          payload.geoJson.features.splice(index, 1, newGeo);
        }
      });
      // 地図にルート形状を追加
      state.map._map.addGeoJsonFigure(payload);
    }
  },

  /**
   * 指定されたIDのポリラインを削除します
   * @param state
   * @param payload
   */
  removePolyline: (state, payload = {}) => {
    state.map._map.removeGeoJsonFigure(payload);
  },

  /**
   * 吹き出しを表示します
   */
  setInfoWindow: (state, payload) => {
    if (!payload) return;
    state.map._map.addInfoWindow(payload);
  },

  /**
   * 吹き出しを削除します
   */
  deleteInfoWindow: (state, payload) => {
    if (!payload) return;
    state.map._map.removeInfoWindow(payload);
  },

  /**
   * /map画面で表示するタブを指定します
   */
  selectedSpotTab: (state, payload) => {
    state.selectedSpotTab = payload;
  },
  setCenterOffset: (state, payload) => {
    state.map.setCenterOffset(payload);
  },
};

export {mutations as default};
